import * as React from 'react';
import { Layout, Menu, Modal, Row, Col, Tooltip, Image, Input, Select  } from 'antd';
import { LeftNav } from '../index';
import { AuthContext } from '../../utilites/auth';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronDown,
} from '@fortawesome/pro-solid-svg-icons';
import './Layout.scss';
import { useCookies } from 'react-cookie';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import ThemeModal from './modals/ThemeModal';
import { UserPreference } from '../../models/user-preference-interface';
import { ThemeIdToNameHelper } from '../../utilites/themes/ThemeSelector';
import { UserPreferenceApi } from '../../apis/user-preference-api';
import { CSPORTAL_APPLICATION_STRING } from '../../utilites/api';
import { Footer } from 'antd/lib/layout/layout';
import FooterLinks from '../FooterLinks';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { AuthApi } from '../../apis/auth-api';
import GovendaCSLogo from '../../assets/GovendaCSLogo.png';
import { faUser } from '@fortawesome/pro-regular-svg-icons';
import { Logger } from '../../utilites/loglySetup';
import axios from 'axios';
import CloseableErrorMessage from '../CloseableErrorMessage';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

const { Header, Content } = Layout;
const { confirm } = Modal;
const { Search } = Input;
const { Option } = Select; 

type Props = {
    children?: React.ReactNode;
    psRef: React.MutableRefObject<PerfectScrollbar | null>
}

const AppLayout: React.FC<Props> = ({
    children,
    psRef
}) => {
    const { setLoggedIn, profile } = React.useContext(AuthContext);
    /* eslint-disable no-unused-vars */
    const [searchType,setSearchType] = useState<string>("all");
    const [cookies, setCookie, removeCookie] = useCookies(['sessionUserName']);
    /* eslint-enable no-unused-vars */
    const [isThemeModalVisible, setThemeModalVisible] = useState<boolean>(false);
    const [isAccessModalVisible, setAccessModalVisible] = useState<boolean>(false);
    const [isSessionTimeoutModalVisible, setSessionTimeoutModalVisible] = useState<boolean>(false);
    const [themeId, setThemeId] = useState<number>(cookies['themeSetting'] !== undefined 
      ? parseInt(cookies['themeSetting']) : 0)
    const history = useHistory();

    useEffect(() =>{
      // observe layout to update perfect scrollbar
      const mainLayoutElement = document.querySelector('.main-layout');
      if (mainLayoutElement) {
          const resizeObserver = new ResizeObserver(() => psRef?.current?.updateScroll())
          resizeObserver?.observe(mainLayoutElement);
      }
    },[profile])

    /**
     * Sign out
     * @param e 
     */
    const handleClick = (e) => {
        if (e.key === 'signout'){
            showConfirm();
        }
    }

    const ChevronDownIcon = (
      <span>
        { profile && (
          <>
            <span>
              <FontAwesomeIcon icon={faUser as IconProp} size="lg" />
            </span>
            <span className="anticon" role="img">
              <FontAwesomeIcon
                icon={faChevronDown as IconProp}
                id="chevronDown"
                className="top-nav-secondary-icon"
                alt="ChevronDownIcon"
              />
            </span>
          </>
        )}
      </span>
    );

    /**
     * Show sign out confirmation
     */
    const showConfirm = () => {
      confirm({
        title: 'Sign Out',
        centered: true,
        icon: <ExclamationCircleOutlined />,
        content: 'Are you sure you want to sign out of Govenda?',
        okText: 'Sign Out',
        cancelText: 'Cancel',
        onOk() {
          signOut();
        }
      });
    };

    const signOut = async () => {
      try {
        await AuthApi.signOut();
        setLoggedIn(false);
        removeCookie('sessionUserName', { path: "/" });
        setCookie('forcedSignOut', true, { path: "/"});
        localStorage.removeItem('currentUser');
        localStorage.removeItem('downloadFileId');

        history.replace("");
      }
      catch (error) {
        if (axios.isCancel(error)) 
          Logger().log(error);
        else 
          Logger().error(error);
        CloseableErrorMessage("Error signing out.");
      }
    };

    const hideThemeModal = () => {
      setThemeModalVisible(false);
    }

    const onThemeSave = async () => {
      const themeValue = ThemeIdToNameHelper(themeId);

      const data: UserPreference = {
        value: themeValue,
        application: CSPORTAL_APPLICATION_STRING,
        key: 'theme',
      };

      try{
        await UserPreferenceApi.patchSingleUserPreference(data);
        setCookie('themeSetting', themeId, {path: "/"});
        setThemeModalVisible(false);
        window.location.reload();
      }
      catch (error) {
        if (axios.isCancel(error)) 
          Logger().log(error);
        else 
          Logger().error(error);
        CloseableErrorMessage("Error updating theme.");
      }      
    }

    const onSearch = (value: string) => {
      if (!value?.length) {
        CloseableErrorMessage("Please supply search text.")
        return;
      }
      let term = encodeURIComponent(value)
      let url = "/admin/search"
      if(searchType === "all") {
        url += `?query=${term}`
      } else if(searchType === "users") {
        url += "/users?query=" + term
      } else if(searchType === "feature") {
        url += "/feature?query=" + term
      } else if(searchType === "subscription") {
        url += "/subscription?query=" + term
      } else if(searchType === "companies") {
        url += "/companies?query=" + term
      } else if(searchType === "companyID" && Number(term) > 0) {
        url = `/admin/company/${Number(term)}`
      } else if(searchType === "userID" && Number(term) > 0) {
        url = `/admin/user/${Number(term)}`
      } else {
        return
      }

      history.push(url)
    }
    
    return (
        <Layout className="main-layout">
            <Header className="main-header">
                <Row wrap={false} align="middle">
                  <Col>
                    <Row wrap={false} align="middle">
                      <Col id="logoHeaderCol">
                        <Tooltip title="Click to go to your dashboard" trigger={['hover', 'focus']} placement="bottom" mouseEnterDelay={0.5}>
                          <Image src={GovendaCSLogo} preview={false} />
                        </Tooltip>
                      </Col>
                    </Row>
                  </Col>
                  <Col flex="auto">
                    <Row justify="end" wrap={false} gutter={[8, 8]}>
                      <Col flex="auto">
                      <Input.Group compact style={{marginTop: '16px', textAlign: 'right'}}>
                        <Select defaultValue={searchType} onChange={v => setSearchType(v)}>
                          <Option value="all">Users and Companies</Option>
                          <Option value="users">Users</Option>
                          <Option value="companies">Companies</Option>
                          <Option value="companyID">Company ID</Option>
                          <Option value="userID">User ID</Option>
                          <Option value="subscription">Subscription</Option>
                          <Option value="feature">Feature</Option>
                        </Select>
                        <Search style={{width: '50%'}} type={searchType === "companyID" || searchType === "userID" ? "number" : "text" } onSearch={onSearch}/>
                      </Input.Group>
                        
                      </Col>
                      <Col id="profileMenu">
                        {/* There is a bug with right justfied menu items not expanding when width expands.
                            disabling overflow as a temporary solution until a mobile design is fully fleshed out.
                        */}
                        <Menu selectable={false} className="Borderless" mode="horizontal" disabledOverflow>
                          <Menu.SubMenu
                            key="Profile"
                            icon={ChevronDownIcon}
                          >
                            <Menu.Item id="signOut" className="Borderless" onClick={handleClick} key="signout">Sign Out</Menu.Item>
                          </Menu.SubMenu>
                        </Menu>
                      </Col>
                    </Row>
                  </Col>
                </Row>
            </Header>
            <Layout>
                <LeftNav />
                <Layout>
                  <Content id="mainContent">
                      {children}
                      <ThemeModal
                        themeId={themeId}
                        isVisible={isThemeModalVisible}
                        hideModal={hideThemeModal}
                        setThemeId={setThemeId}
                        onThemeSave={onThemeSave}
                      />
                  </Content>
                  <Footer>
                      <p>Govenda CS Portal</p>
                      <FooterLinks /> 
                  </Footer>                  
                </Layout>
            </Layout>
        </Layout>
    )};

export default AppLayout;